import React, { useContext, useEffect, useRef, useState } from "react";
import { Platform, Pressable, StyleSheet, View } from "react-native";
import { Camera } from 'react-native-camera-kit';
import { useSafeAreaInsets } from "react-native-safe-area-context";
import Icon from "react-native-vector-icons/Ionicons";
import { IconShadow } from "./helpers/DefaultShadows";
import { FindifyDataContext, FindifyDataContextType } from "@/contexts/FindifyDataContext";
import { NavigationProp, useNavigation } from "@react-navigation/native";
import { FindifyDialog } from "./components/FindifyDialog";
import { useTranslation } from "react-i18next";
import { PlaceText } from "./components/PlaceText";
import { PlaceColors } from "./helpers/PlaceColors";

const loadQRWorkspace = (params: any, findifyDataContext: FindifyDataContextType) => {
    const desiredWS = findifyDataContext.availableWorkspaces.find((workspace: any) => {
        return workspace.workspace_id.toString() === params.workspace_id
    });
    if (desiredWS) {
        // SET IT 
        findifyDataContext.setWorkspace(desiredWS);
        findifyDataContext.setVisitWorkspaceSelected(true);
    }
}

const loadQRAction = (params: any, findifyDataContext: FindifyDataContextType, navigation: any) => {
    // This method only should happend after workspace is selected
    setTimeout(() => {
        switch (params.type) {
            case 'details':
                navigation.replace('WorkspacePage', {
                    screen: 'PlaceContentDetails',
                    params: {
                        module: params.module,
                        content_id: params.content_id,
                    }
                    // page_slug: content.page_slug
                })
                break;
            case 'list':
                navigation.replace('WorkspacePage', {
                    screen: 'PlaceContentList',
                    params: {
                        module: params.module,
                        category: params.category_id
                    }
                });
                break;
            default:
                break;
        }

        // IF Nothing, homepage?
    }, 200);
}

export const processQRLinkParams = (urlParams: String, findifyDataContext: FindifyDataContextType, navigation: any, setDialogCode: any, t: any) => {
    if (urlParams) {
        const urlParamsOBJ = urlParams.split('&');
        let params: any = {};
        urlParamsOBJ.forEach((param) => {
            const [key, value] = param.split('=');
            params[key] = value;
        });
    
        if (findifyDataContext.visitWorkspaceSelected) {
            if (findifyDataContext.workspace.workspace_id.toString() === params.workspace_id) {
                // QR FROM CURRENT WS
                loadQRAction(params, findifyDataContext, navigation);
            } else {
                // QR FROM ANOTHER WS
                setDialogCode(<FindifyDialog
                    action1Text={t('popup.continue')}
                    onAction1={() => {
                        // Continue scan, just close
                        loadQRWorkspace(params, findifyDataContext);
                        loadQRAction(params, findifyDataContext, navigation);
                    }}
                    action2Text={t('popup.cancel')}
                    onAction2={() => {
                        // close scan page, go back
                        setDialogCode(null)
                        navigation.goBack();
                    }}
                    message={t('scan_tab.qr_another_place_message')} />)
            }
        } else {
            // NO WORKSPACE SELECTED
            // Load WS
            loadQRWorkspace(params, findifyDataContext);
            loadQRAction(params, findifyDataContext, navigation);
        }
    }
}
const qrCodeProcessing = (url: string, setURLParams: any, navigation: NavigationProp<any>, setDialogCode: any, t: any, setScanningQR: any) => {
    // TODO DELETE HARDCODED
    // url = 'https://braganoivos.findify.cloud/dynamicLink?workspace_id=38&type=list&module=events&category_id=42';// List Example with category filter
    // url = 'https://braganoivos.findify.cloud/dynamicLink?workspace_id=38&type=details&module=events&content_id=24';// Content Details
    // url = 'https://braganoivos.findify.cloud/dynamicLink?workspace_id=38';// Place Homepage


    if (url.includes('findify.cloud/dynamicLink')) {
        setURLParams(url.split('?')[1]);
    } else {
        // Invalid QR
        setDialogCode(<FindifyDialog
            onAction1={() => {
                // Continue scan, just close
                setDialogCode(null);
                setScanningQR(false);
            }}
            onAction2={() => {
                // close scan page, go back
                setDialogCode(null)
                navigation.goBack();
            }}
            action1Text={t('popup.continue')}
            action2Text={t('popup.cancel')}
            message={t('scan_tab.invalid_qr_message')} />)
    }
}

const WebQrScanner = ({
    onCode,
    onBack,
    findifyDataContext,
}: {
    onCode: (value: string) => void,
    onBack: () => void,
    findifyDataContext: FindifyDataContextType,
}) => {
    const videoRef = useRef<any>(null);
    const streamRef = useRef<MediaStream | null>(null);
    const scanningRef = useRef(true);
    const [error, setError] = useState('');
    const colors = PlaceColors(findifyDataContext);

    useEffect(() => {
        let frameId = 0;
        let detector: any;

        const stopCamera = () => {
            scanningRef.current = false;
            if (frameId) cancelAnimationFrame(frameId);
            streamRef.current?.getTracks?.().forEach((track) => track.stop());
            streamRef.current = null;
        }

        const start = async () => {
            try {
                if (!('mediaDevices' in navigator) || !navigator.mediaDevices?.getUserMedia) {
                    setError('Este browser não permite acesso à câmara.');
                    return;
                }
                if (!('BarcodeDetector' in window)) {
                    setError('O leitor de QR não é suportado neste browser. Usa a opção de leitura nativa do dispositivo ou atualiza o Chrome.');
                    return;
                }

                detector = new (window as any).BarcodeDetector({ formats: ['qr_code'] });
                const stream = await navigator.mediaDevices.getUserMedia({
                    video: { facingMode: { ideal: 'environment' } },
                    audio: false,
                });
                streamRef.current = stream;
                if (videoRef.current) {
                    videoRef.current.srcObject = stream;
                    await videoRef.current.play();
                }

                const scan = async () => {
                    if (!scanningRef.current || !videoRef.current) return;
                    try {
                        const codes = await detector.detect(videoRef.current);
                        const rawValue = codes?.[0]?.rawValue;
                        if (rawValue) {
                            stopCamera();
                            onCode(rawValue);
                            return;
                        }
                    } catch (scanError) {
                        // Keep scanning; transient detector errors happen while video metadata loads.
                    }
                    frameId = requestAnimationFrame(scan);
                }
                frameId = requestAnimationFrame(scan);
            } catch (cameraError) {
                console.error('Failed to start QR camera:', cameraError);
                setError('Não foi possível abrir a câmara. Confirma as permissões do browser.');
            }
        }

        start();
        return stopCamera;
    }, [onCode]);

    return <View style={[styles.webScannerPage, { backgroundColor: colors.background.get() }]}>
        {React.createElement('video', {
            ref: videoRef,
            muted: true,
            playsInline: true,
            style: {
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                backgroundColor: '#000',
            },
        })}
        <View style={styles.webScannerOverlay}>
            <Pressable style={[styles.webBackButton, { backgroundColor: colors.primary.withAlpha(210) }]} onPress={onBack}>
                <Icon name="chevron-back" size={30} color="#fff" />
            </Pressable>
            <View style={styles.webScannerFrame} />
            <PlaceText style={styles.webScannerText}>Aponte a câmara para o QR code</PlaceText>
            {!!error && <View style={[styles.webScannerError, { backgroundColor: colors.background.withAlpha(235) }]}>
                <PlaceText style={[styles.webScannerErrorText, { color: colors.primary.get() }]}>{error}</PlaceText>
            </View>}
        </View>
    </View>
}

export const ScanQrTab = ({ navigation, props }: any) => {
    const insets = useSafeAreaInsets();
    const findifyDataContext = useContext(FindifyDataContext);
    const [URLParams, setURLParams] = useState<String>('');
    const [scanningQR, setScanningQR] = useState<Boolean>(false);
    const [dialogCode, setDialogCode] = useState<JSX.Element | null>(null);
    const { t } = useTranslation();
    const nav = useNavigation();

    useEffect(() => {
        processQRLinkParams(URLParams, findifyDataContext, nav, setDialogCode, t);
    }, [URLParams, nav])

    const goBackOrHome = () => {
        if (navigation?.canGoBack?.()) {
            navigation.goBack();
            return;
        }
        navigation.navigate('Place');
    }

    if (Platform.OS === 'web') {
        return <>
            <WebQrScanner
                findifyDataContext={findifyDataContext as FindifyDataContextType}
                onBack={goBackOrHome}
                onCode={(qrValue) => {
                    if (!scanningQR) {
                        setScanningQR(true);
                        qrCodeProcessing(qrValue, setURLParams, nav, setDialogCode, t, setScanningQR);
                    }
                }}
            />
            {dialogCode}
        </>
    }

    return (
        (
            <>
                <View style={styles.camera}>
                    <Camera
                        showFrame={true}
                        frameColor='white'
                        scanBarcode={true}
                        onReadCode={(code: any) => {
                            if (!scanningQR) {
                                setScanningQR(true);
                                const qrValue = code?.nativeEvent?.codeStringValue;
                                if (qrValue) {
                                    // QR FOUND - DO SOMETHING
                                    console.log('codenecsv', qrValue);
                                    qrCodeProcessing(qrValue, setURLParams, nav, setDialogCode, t, setScanningQR);
                                }
                            }
                        }}
                        style={styles.camera}
                    />
                    <View style={{ position: 'absolute', top: insets.top, left: 0 }}>
                        {navigation?.canGoBack() &&
                            <Icon style={[styles.goBackIcon]} name="chevron-back" size={30} color="white" onPress={() => {
                                goBackOrHome();
                            }} />}
                    </View>
                </View>
                {dialogCode}
            </>
        )
    );

};


const styles = StyleSheet.create({
    overlayContainer: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
    },
    transparentBackground: {
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        width: '100%',
        height: '100%',
        position: 'absolute',
    },
    transparentBox: {
        borderWidth: 2,
        borderColor: 'white',
        width: 200, // Adjust the width of the transparent box as needed
        height: 200, // Adjust the height of the transparent box as needed
        backgroundColor: 'transparent',
    },
    qrOverlay: {
        width: '100%',
        height: '100%',
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
    },
    camera: {
        flex: 1
    },
    container: {
        flex: 1
    },
    goBackIcon: {
        padding: 5,
        ...IconShadow,
    },
    webScannerPage: {
        flex: 1,
        width: '100%',
        height: '100%',
        position: 'relative',
        overflow: 'hidden',
    },
    webScannerOverlay: {
        ...StyleSheet.absoluteFillObject,
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(0,0,0,0.22)',
        padding: 24,
    },
    webBackButton: {
        position: 'absolute',
        top: 18,
        left: 18,
        width: 48,
        height: 48,
        borderRadius: 24,
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 2,
    },
    webScannerFrame: {
        width: 240,
        height: 240,
        borderWidth: 3,
        borderColor: '#fff',
        borderRadius: 18,
        backgroundColor: 'transparent',
        shadowColor: '#000',
        shadowOpacity: 0.35,
        shadowRadius: 14,
        shadowOffset: { width: 0, height: 6 },
    },
    webScannerText: {
        marginTop: 22,
        color: '#fff',
        fontSize: 16,
        lineHeight: 22,
        fontWeight: '700',
        textAlign: 'center',
        textShadowColor: 'rgba(0,0,0,0.45)',
        textShadowOffset: { width: 0, height: 1 },
        textShadowRadius: 4,
    },
    webScannerError: {
        position: 'absolute',
        left: 24,
        right: 24,
        bottom: 42,
        borderRadius: 8,
        padding: 14,
    },
    webScannerErrorText: {
        fontSize: 14,
        lineHeight: 20,
        fontWeight: '600',
        textAlign: 'center',
    },
})
